import Promise from 'promise-polyfill';
import { filterXSS } from 'xss';

(() => {
  function buildResponseObject(code, message, data) {
    return {
      responseCode: code,
      responseMessage: message,
      data,
    };
  }

  function injectCSS() {
    const css = `
      .pit-loader-label {
        display: none;
      }
      .pit-loader-container {
        background-color: #FFFFFF;
        border-radius: 12px;
        box-shadow: 0 2px 4px 0 rgba(0,0,0,0.2);
        height: 128px;
        left: 50%;
        top: 40%;
        position: absolute;
        padding: 64px;
        transform: translate(-50%, -50%);
        width: 128px;
      }
      .pit-loader-outer,
      .pit-loader-inner {
        border-radius: 2000px;
        animation-direction: alternate;
        animation-duration: 0.75s;
        animation-iteration-count: infinite;
        animation-name: loader;
        left: 50%;
        position: absolute;
        top: 50%;
        transform-origin: 0 0;
      }
      .pit-loader-outer {
        animation-delay: 0.125s;
        height: 64px;
        width: 64px;
        z-index: 1;
        transform: scale(1) translate(-50%, -50%);
      }
      .pit-loader-inner {
        background-color: #FFFFFF;
        height: 56px;
        width: 56px;
        z-index: 2;
        transform: scale(1) translate(-50%, -50%);
      }
      @keyframes loader {
        from {
          transform: scale(1) translate(-50%, -50%);
        }
        to {
          transform: scale(0.6) translate(-50%, -50%);
        }
      }
      .payit_iframe_container {
        background-color: #F5F5F5;
        border: 1px solid #E5E5E5;
        bottom: 0;
        box-shadow: 0 -2px 4px 0 rgba(0,0,0,0.2);
        height: 100%;
        left: 0;
        position: fixed;
        right: 0;
        transform: translateY(100%);
        transition: transform .125s;
        top: 0;
        width: 100%;
        z-index: 20000;
      }
      .payit_iframe_container-active {
        transform: translateY(0);
      }
      .payit_form { display: none; }
    `;
    const styleTag = document.createElement('style');
    styleTag.innerHTML = css;
    document.head.appendChild(styleTag);
  }

  function hasOwnPropertyHelper(object, propertyName) {
    return Object.prototype.hasOwnProperty.call(object, propertyName);
  }

  function validateEnvironment(environment) {
    const environments = {
      SANDBOX: 'SANDBOX',
      TEST: 'TEST',
      PRODUCTION: 'PRODUCTION',
      LOCAL: 'LOCAL',
      CA_PRODUCTION: 'CA_PRODUCTION',
      CA_TEST: 'CA_TEST'
    };
    if (!environments[environment]) {
      return `Invalid environment: ${environment} was specified`;
    }
    return undefined;
  }

  function getBaseConfigValidation() {
    return [
      config => hasOwnPropertyHelper(config, 'appName')
        ? undefined
        : 'appName is required',
      config => hasOwnPropertyHelper(config, 'environment')
        ? undefined
        : 'environment is required.',
      config => validateEnvironment(config.environment),
    ];
  }

  function getAuthenticationValidation() {
    return [
      config => (hasOwnPropertyHelper(config, 'authToken')
        || hasOwnPropertyHelper(config, 'requireAuthentication'))
        ? undefined
        : 'authToken or "requireAuthentication" is required',
    ];
  }

  function validateConfig(config, customValidators) {
    const errors = [];
    const baseConfigValidation = getBaseConfigValidation();
    baseConfigValidation.forEach((validator) => {
      const validationMessage = validator(config);
      if (validationMessage) {
        errors.push(validationMessage);
      }
    });
    if (customValidators && Array.isArray(customValidators)) {
      customValidators.forEach((validator) => {
        const validationMessage = validator(config);
        if (validationMessage) {
          errors.push(validationMessage);
        }
      });
    }
    if (!errors.length) {
      return undefined;
    }
    return errors;
  }

  function getBaseURLByEnvironment(environment) {
    const urls = {
      SANDBOX: 'https://api-dev.mobilgov.com',
      TEST: 'https://api-staging.mobilgov.com',
      CA_TEST: 'https://api-staging.payitgov.ca',
      SYSTEM: 'https://api-system.payitgov.com',
      PRODUCTION: 'https://api.mobilgov.com',
      CA_PRODUCTION: 'https://api.payitgov.ca',
      LOCAL: 'http://localhost:3000',
    };
    return urls[environment];
  }

  function getEnvironmentNameFromPublicName(name) {
    const environmentNames = {
      PRODUCTION: 'PROD',
      CA_PRODUCTION: 'CA_PROD',
      TEST: 'STAGING',
      CA_TEST: 'CA_STAGING',
      SYSTEM: 'SYSTEM',
      SANDBOX: 'DEV',
      LOCAL: 'DEV',
    };
    return environmentNames[name];
  }

  function buildLoaderDOM(brandColor) {
    let _brandColor = brandColor;
    if (!brandColor) {
      _brandColor =  '#026CFD';
    }
    const loadingPulseOuter = document.createElement('div');
    loadingPulseOuter.classList.add('pit-loader-outer');
    loadingPulseOuter.setAttribute('style', `background-color: ${_brandColor}`);
    const loadingPulseInner = document.createElement('div');
    loadingPulseInner.classList.add('pit-loader-inner');
    const loadingA11yLabel = document.createElement('p');
    loadingA11yLabel.classList.add('pit-loader-label');
    const container = document.createElement('div');
    container.classList.add('pit-loader-container');
    container.appendChild(loadingPulseOuter);
    container.appendChild(loadingPulseInner);
    container.appendChild(loadingA11yLabel);
    return container;
  }

  function getIFrame(name) {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('name', name);
    iframe.classList.add('payit_iframe_container');
    setTimeout(() => {
      iframe.classList.add('payit_iframe_container-active');
    }, 125);
    return iframe;
  }

  function invokeIFrame(config, route, disallowInBodyList) {
    const IFRAME_NAME = 'payitIFrame';
    const formToPostToFrame = document.createElement('form');
    formToPostToFrame.setAttribute('method', 'post');
    formToPostToFrame.classList.add('payit_form');
    formToPostToFrame.setAttribute(
      'action',
      `${getBaseURLByEnvironment(config.environment)}${route}?environment=${getEnvironmentNameFromPublicName(config.environment)}`
    );
    Object.keys(config).forEach((key) => {
      if (disallowInBodyList && disallowInBodyList.includes(key)) {
        return;
      }
      const input = document.createElement('input');
      input.setAttribute('name', key);
      let value;
      if (typeof config[key] !== 'string') {
        value = JSON.stringify(config[key]);
      } else {
        value = config[key];
      }
      input.setAttribute('value', filterXSS(value));
      formToPostToFrame.appendChild(input);
    });
    formToPostToFrame.setAttribute('target', IFRAME_NAME);

    const iframe = getIFrame(IFRAME_NAME);
    iframe.setAttribute('allow', `geolocation ${getBaseURLByEnvironment(config.environment)};`);
    document.body.appendChild(iframe);
    document.body.appendChild(formToPostToFrame);
    formToPostToFrame.submit();
    document.body.removeChild(formToPostToFrame);
    return iframe;
  }
  function destroyIFrame(iframe) {
    iframe.classList.remove('payit_iframe_container-active');
    setTimeout(
      () => document.body.removeChild(iframe),
      125
    );
  }

  /*
    Invokes a checkout view within an iFrame
    Expects data = {
      authToken: string(optional),
      brandColor: string(optional),
      cartId: string(optional),
      requireAuthentication: boolean(optional),
      emailAddress: string,
      environment: string,
      lineItems: [
        {
          label: string,
          description: string,
          value: int(pennies),
          serviceOfferingId: string,
          uniqueExternalId: string,
          itemization: [],
        },
      ],
    }
    Returns a promise
  */
  function checkout(config) {
    const methodConfigValidation = [
      config => hasOwnPropertyHelper(config, 'emailAddress')
        ? undefined
        : 'emailAddress is required',
      config => hasOwnPropertyHelper(config, 'line_items')
        ? undefined
        : 'line_items are required',
    ];
    return new Promise((resolve, reject) => {
      const configErrors = validateConfig(config, methodConfigValidation);
      if (configErrors) {
        return reject(buildResponseObject(2, `Invalid configuration ${JSON.stringify(configErrors)}`));
      }
      const refToIframe = invokeIFrame(
        config,
        `/sdk/app/${config.appName}/checkout`
      );
      const loadingIndicator = buildLoaderDOM(config.brandColor);
      document.body.appendChild(loadingIndicator);
      window.onmessage = (event) => {
        const response = (typeof event.data === 'object')
          ? event.data
          : JSON.parse(event.data);
        if (response.responseCode != undefined) {
          if (response.responseCode === 3) {
            document.body.removeChild(loadingIndicator);
            return;
          }
          if (response.responseCode === 1) {
            resolve(response);
          } else {
            reject(response);
          }
          destroyIFrame(refToIframe);
        }
      };
    });
  }

  /**
   * @typedef CartConfigObject
   * @property {string} appName
   * @property {string} authToken
   * @property {string} cartId
   * @property {string} environment
   */
  /**
   * Invokes checkout with a temp cart ID created by the server
   * @param {CartConfigObject} config
   * @returns {Promise}
   */
  function cart(config) {
    const methodConfigValidation = [
      config => hasOwnPropertyHelper(config, 'authToken')
        ? undefined
        : 'authToken is required',
      config => hasOwnPropertyHelper(config, 'cartId')
        ? undefined
        : 'cartId is required',
    ];
    return new Promise((resolve, reject) => {
      const configErrors = validateConfig(config, methodConfigValidation);
      if (configErrors) {
        reject(buildResponseObject(2, `Invalid configuration ${JSON.stringify(configErrors)}`));
      }
      const refToIframe = invokeIFrame(
        config,
        `/sdk/app/${config.appName}/checkout/${config.cartId}`,
        [ 'environment', 'appName', ]
      );
      const loadingIndicator = buildLoaderDOM(config.brandColor);
      window.onmessage = (event) => {
        const response = JSON.parse(event.data);
        if (response.responseCode === 3) {
          document.body.removeChild(loadingIndicator);
          return;
        }
        if (response.responseCode === 1) {
          resolve(response);
        } else {
          reject(response);
        }
        destroyIFrame(refToIframe);
      }
    })
  }

  /*
    Invokes a checkout view within an iFrame
    Expects data = {
      authToken: string(optional),
      requireAuthentication: boolean(optional),
      brandColor: string(optional),
      environment: string,
    }
    Returns a promise
  */
  function paymentMethods(config) {
    const methodConfigValidation = getAuthenticationValidation();
    const configErrors = validateConfig(config, methodConfigValidation);
    return new Promise((resolve, reject) => {
      if (configErrors) {
        return reject(buildResponseObject(2, `Invalid configuration ${JSON.stringify(configErrors)}`));
      }
      const refToIframe = invokeIFrame(
        config,
        `/sdk/app/${config.appName}/payment-methods`
      );
      const loadingIndicator = buildLoaderDOM(config.brandColor);
      document.body.appendChild(loadingIndicator);
      window.onmessage = (event) => {
        const response = JSON.parse(event.data);
        if (response.responseCode === 3) {
          document.body.removeChild(loadingIndicator);
          return;
        }
        if (response.responseCode === 1) {
          resolve(response);
        } else {
          reject(response);
        }
        destroyIFrame(refToIframe);
      };
    });
  }

  /*
    Invokes a document view within an iFrame
    Expects data = {
      authToken: string(optional),
      requireAuthentication: boolean(optional),
      brandColor: string(optional),
      environment: string,
    }
    Returns a promise
  */
  function documents(config) {
    const methodConfigValidation = getAuthenticationValidation();
    const configErrors = validateConfig(config, methodConfigValidation);
    return new Promise((resolve, reject) => {
      if (configErrors) {
        return reject(buildResponseObject(2, `Invalid configuration ${JSON.stringify(configErrors)}`));
      }
      const refToIframe = invokeIFrame(
        config,
        `/sdk/app/${config.appName}/documents`
      );
      const loadingIndicator = buildLoaderDOM(config.brandColor);
      document.body.appendChild(loadingIndicator);
      window.onmessage = (event) => {
        const response = JSON.parse(event.data);
        if (response.responseCode === 3) {
          document.body.removeChild(loadingIndicator);
          return;
        }
        if (response.responseCode === 1) {
          resolve(response);
        } else {
          reject(response);
        }
        destroyIFrame(refToIframe);
      };
    });
  }

  /*
    Invokes a receipt view within an iFrame
    Expects data = {
      authToken: string(optional),
      requireAuthentication: boolean(optional),
      brandColor: string(optional),
      environment: string,
    }
    Returns a promise
  */
  function receipts(config) {
    const methodConfigValidation = getAuthenticationValidation();
    const configErrors = validateConfig(config, methodConfigValidation);
    return new Promise((resolve, reject) => {
      if (configErrors) {
        return reject(buildResponseObject(2, `Invalid configuration ${JSON.stringify(configErrors)}`));
      }
      const refToIframe = invokeIFrame(
        config,
        `/sdk/app/${config.appName}/receipts`
      );
      const loadingIndicator = buildLoaderDOM(config.brandColor);
      document.body.appendChild(loadingIndicator);
      window.onmessage = (event) => {
        const response = JSON.parse(event.data);
        if (response.responseCode === 3) {
          document.body.removeChild(loadingIndicator);
          return;
        }
        if (response.responseCode === 1) {
          resolve(response);
        } else {
          reject(response);
        }
        destroyIFrame(refToIframe);
      };
    });
  }

  injectCSS();
  const exposedInterface = {
    cart,
    checkout,
    paymentMethods,
    documents,
    receipts
  };
  if (window) {
    window.payit = exposedInterface;
  }
})();
